import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Premium kit — Phase 3 (improvement plan, steps 1–2).
 *
 * cva primitives over the token layer at the end of `src/app/globals.css`.
 * During the redesign rollout, convert ad-hoc inline styles and raw-CSS
 * classes to these primitives (see docs/redesign-playbook.md). Server-safe:
 * no client hooks — usable in RSC and client components alike.
 */

const surfaceVariants = cva(
  "rounded-[var(--radius-xl)] border transition-[box-shadow,transform,border-color,background-color] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]",
  {
    variants: {
      variant: {
        card: "bg-[var(--color-paper)] border-[var(--color-border)] surface-elevation-2",
        raised:
          "bg-[var(--color-paper)] border-[var(--color-border)] surface-elevation-3",
        /* Opaque ink panel with a hairline. Glass blur is banned (design.md §1). */
        ink:
          "bg-[var(--color-ink-800)] text-[var(--color-ink-fg)] border-[var(--color-ink-line-strong)] shadow-[var(--elevation-ink)]",
        sunken:
          "bg-[var(--color-paper-deep)] border-[var(--color-border)] shadow-none",
        inverse:
          "bg-[var(--color-espresso)] text-[var(--color-paper)] border-transparent surface-elevation-3",
      },
      elevation: {
        1: "shadow-[var(--elevation-1)]",
        2: "shadow-[var(--elevation-2)]",
        3: "shadow-[var(--elevation-3)]",
        4: "shadow-[var(--elevation-4)]",
      },
      interactive: {
        true: "hover:surface-elevation-4 hover:-translate-y-0.5 active:scale-[0.98] active:translate-y-0 cursor-pointer motion-reduce:transform-none motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100",
        false: "",
      },
      padding: {
        none: "p-0",
        sm: "p-4",
        md: "p-6",
        lg: "p-8",
      },
    },
    defaultVariants: { variant: "card", interactive: false, padding: "md" },
  },
);

export function Surface({
  className,
  variant,
  elevation,
  interactive,
  padding,
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof surfaceVariants> & {
    elevation?: 1 | 2 | 3 | 4;
  }) {
  return (
    <div
      data-surface={variant === "ink" ? "ink" : undefined}
      className={cn(surfaceVariants({ variant, elevation, interactive, padding }), className)}
      {...props}
    />
  );
}

/** Uppercase mono eyebrow label — replaces the repeated `monoMeta` inline-style object pattern. */
export function Eyebrow({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "font-mono text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--color-fg-subtle)] inline-flex items-center gap-1.5",
        className,
      )}
      {...props}
    />
  );
}

const headingVariants = cva("text-[var(--color-fg)] font-semibold tracking-[-0.035em] text-balance", {
  variants: {
    level: {
      display: "text-[clamp(2.25rem,5vw,3.5rem)] leading-[1.05]",
      section: "text-[clamp(1.75rem,3.5vw,2.5rem)] leading-[1.1]",
      block: "text-[22px] leading-[1.2]",
    },
  },
  defaultVariants: { level: "section" },
});

export function SectionHeading({
  className,
  level,
  as: Tag = "h2",
  ...props
}: React.ComponentProps<"h2"> &
  VariantProps<typeof headingVariants> & { as?: "h1" | "h2" | "h3" | "h4" }) {
  return <Tag className={cn(headingVariants({ level }), className)} {...props} />;
}

/**
 * Long-form prose container — the Tailwind replacement for the retired
 * `legal.css` descendant rules. Use for legal pages and other editorial text.
 */
export function Prose({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "max-w-[680px] text-[15.5px] leading-[1.65] text-[var(--color-fg-muted)]",
        "[&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-[22px] [&_h2]:font-semibold [&_h2]:leading-[1.2] [&_h2]:tracking-[-0.035em] [&_h2]:text-[var(--color-fg)]",
        "[&_p]:mb-4 [&_ul]:mb-4 [&_ul]:pl-5 [&_li]:mb-2",
        "[&_strong]:font-semibold [&_strong]:text-[var(--color-fg)]",
        "[&_a]:text-[var(--color-develop)] [&_a]:underline [&_a]:underline-offset-[3px]",
        className,
      )}
      {...props}
    />
  );
}

/* ── Marketing shell (Step 2) — badge + section layout primitives ───────── */

const badgeVariants = cva(
  "badge inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-sans text-xs font-medium leading-snug tracking-[-0.005em] transition-all duration-150 ease-out active:scale-[0.98] motion-reduce:active:scale-100",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--color-badge-blue-bg)] text-[var(--color-badge-blue-fg)] border border-transparent",
        gray:
          "badge-gray bg-[var(--color-surface-1)] text-[var(--color-fg-muted)] shadow-[var(--shadow-light-ring)] border border-[var(--color-border)]",
        pink:
          "badge-pink bg-[var(--color-badge-pink-bg)] text-[var(--color-badge-pink-fg)] border border-transparent",
        red:
          "badge-red bg-[var(--color-badge-red-bg)] text-[var(--color-badge-red-fg)] border border-transparent",
        outline:
          "border border-[var(--color-border)] bg-transparent text-[var(--color-fg)]",
        success:
          "border border-transparent bg-[color-mix(in_srgb,var(--color-success)_14%,transparent)] text-[var(--color-success)]",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export function Badge({
  className,
  variant,
  dot = false,
  dotClassName,
  children,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    dot?: boolean;
    dotClassName?: string;
  }) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot ? (
        <span
          aria-hidden="true"
          className={cn("size-1.5 shrink-0 rounded-full bg-current", dotClassName)}
        />
      ) : null}
      {children}
    </span>
  );
}

/** Full-width marketing section band — replaces `.section`. */
export function MarketingSection({ className, ...props }: React.ComponentProps<"section">) {
  return (
    <section
      className={cn("relative px-5 py-20 sm:px-8 lg:px-12 lg:py-24 max-[480px]:py-14", className)}
      {...props}
    />
  );
}

/** Centered content column — replaces `.section-inner`. */
export function MarketingSectionInner({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("mx-auto max-w-[1240px]", className)} {...props} />;
}

/** Centred section intro block — replaces `.section-head`. */
export function MarketingSectionHead({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("mb-12 max-w-[820px] text-left max-[834px]:mb-9", className)}
      {...props}
    />
  );
}

/** Monumental display title — replaces `.section-title-xl`. */
export function MarketingDisplayTitle({
  className,
  as: Tag = "h2",
  ...props
}: React.ComponentProps<"h2"> & { as?: "h1" | "h2" | "h3" }) {
  return (
    <Tag
      className={cn(
        "section-title-xl mb-4 font-sans text-[clamp(2.375rem,5vw,4rem)] font-semibold leading-[1.06] tracking-[-0.05em] text-[var(--color-ink)]",
        "[&_em]:font-medium [&_em]:not-italic [&_em]:text-[var(--color-fg-muted)]",
        className,
      )}
      {...props}
    />
  );
}

/** Section supporting copy — replaces `.section-lead`. */
export function MarketingSectionLead({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      className={cn(
        "section-lead text-lg font-normal leading-[1.55] tracking-[-0.01em] text-[var(--color-fg-muted)] max-[834px]:text-base",
        className,
      )}
      {...props}
    />
  );
}

/* ── Home SaaS (Step 3) — homepage primitives from home-saas.css ─────────── */

/** Lime-dot mono kicker — replaces `.saas-kicker`. */
export function SaasKicker({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      className={cn(
        "saas-kicker mb-[18px] inline-flex items-center gap-2 font-mono text-[0.6875rem] font-[650] uppercase leading-none tracking-[0.13em] text-[var(--color-badge-blue-fg)]",
        "before:size-[7px] before:shrink-0 before:rounded-full before:border before:border-[color-mix(in_srgb,var(--color-accent)_78%,var(--color-ink))] before:bg-[var(--color-accent)] before:content-['']",
        className,
      )}
      {...props}
    />
  );
}

/** Centered home column — replaces `.saas-container` / `.saas-narrow`. */
export function SaasContainer({
  className,
  narrow = false,
  ...props
}: React.ComponentProps<"div"> & { narrow?: boolean }) {
  return (
    <div
      className={cn(
        "saas-container mx-auto w-[min(1200px,calc(100%-40px))] max-[680px]:w-[min(calc(100%-32px),1200px)]",
        narrow && "saas-narrow max-w-[760px]",
        className,
      )}
      {...props}
    />
  );
}

/** Full-width home band — replaces `.saas-section`. */
export function SaasSection({ className, ...props }: React.ComponentProps<"section">) {
  return (
    <section
      className={cn("saas-section py-[clamp(72px,9vw,128px)]", className)}
      {...props}
    />
  );
}

/** Home section intro — replaces `.saas-section-head`. */
export function SaasSectionHead({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "saas-section-head max-w-[580px]",
        "[&_h2]:m-0 [&_h2]:text-[clamp(2.1rem,3.7vw,3.45rem)] [&_h2]:font-[570] [&_h2]:leading-[1.02] [&_h2]:tracking-[-0.055em] [&_h2]:text-[var(--color-ink)]",
        "[&>p:not(.saas-kicker)]:mt-5 [&>p:not(.saas-kicker)]:text-base [&>p:not(.saas-kicker)]:leading-[1.62] [&>p:not(.saas-kicker)]:text-[var(--color-fg-muted)]",
        className,
      )}
      {...props}
    />
  );
}

/** FAQ accordion stack — replaces `.saas-faq-list`. */
export function SaasFaqList({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "saas-faq-list mt-[31px] border-t border-[var(--color-border)]",
        "[&_details]:border-b [&_details]:border-[var(--color-border)]",
        "[&_summary]:flex [&_summary]:min-h-[62px] [&_summary]:cursor-pointer [&_summary]:list-none [&_summary]:items-center [&_summary]:justify-between [&_summary]:text-[0.93rem] [&_summary]:font-[620] [&_summary]:text-[var(--color-ink)]",
        "[&_summary::-webkit-details-marker]:hidden",
        "[&_summary]:after:text-[1.2rem] [&_summary]:after:font-[450] [&_summary]:after:text-[var(--color-develop)] [&_summary]:after:content-['+']",
        "[&_details[open]_summary]:after:content-['−']",
        "[&_summary]:focus-visible:outline [&_summary]:focus-visible:outline-[3px] [&_summary]:focus-visible:outline-offset-[3px] [&_summary]:focus-visible:outline-[color-mix(in_srgb,var(--color-focus)_28%,transparent)]",
        "[&_p]:m-0 [&_p]:max-w-[65ch] [&_p]:pb-[21px] [&_p]:text-[0.88rem] [&_p]:leading-[1.6] [&_p]:text-[var(--color-fg-muted)]",
        className,
      )}
      {...props}
    />
  );
}

/** Three-up Soft Spatial price grid — Free / Fix Pack / Pro. */
export function SaasPriceGrid({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "saas-price-grid mt-[38px] grid grid-cols-4 gap-3.5 max-[1120px]:grid-cols-2 max-[640px]:grid-cols-1",
        className,
      )}
      {...props}
    />
  );
}

/** Price card — Soft Spatial raised paper; featured gets signal border. */
export function SaasPriceCard({
  className,
  featured = false,
  ...props
}: React.ComponentProps<"div"> & { featured?: boolean }) {
  return (
    <div
      className={cn(
        "saas-price-card flex min-h-[302px] flex-col rounded-[16px] border border-[var(--color-border)] bg-[var(--color-paper)] p-[clamp(24px,3.2vw,38px)] max-[900px]:min-h-0",
        "transition-[transform,box-shadow,border-color] duration-[220ms] ease-[ease] motion-reduce:transition-none",
        "hover:border-[color-mix(in_srgb,var(--color-focus)_26%,var(--color-border))] hover:shadow-[var(--elevation-3)] hover:-translate-y-[3px] motion-reduce:hover:translate-y-0",
        "[&_h3]:m-0 [&_h3]:text-base [&_h3]:font-[650] [&_h3]:text-[var(--color-ink)]",
        "[&_ul]:mb-[25px] [&_ul]:grid [&_ul]:list-none [&_ul]:gap-[11px] [&_ul]:p-0 [&_ul]:text-[0.86rem] [&_ul]:leading-[1.45] [&_ul]:text-[var(--color-fg-muted)]",
        "[&_li]:relative [&_li]:pl-4",
        "[&_li]:before:absolute [&_li]:before:top-[0.55em] [&_li]:before:left-0 [&_li]:before:size-[5px] [&_li]:before:rounded-full [&_li]:before:bg-[var(--color-accent)] [&_li]:before:content-['']",
        featured &&
          "border-[color-mix(in_srgb,var(--color-focus)_32%,var(--color-border))] bg-[linear-gradient(165deg,color-mix(in_srgb,var(--color-accent)_7%,transparent),transparent_48%),var(--color-paper)] shadow-[var(--elevation-3)]",
        className,
      )}
      {...props}
    />
  );
}

/** Monumental price figure — replaces `.saas-price`. */
export function SaasPrice({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      className={cn(
        "saas-price my-3.5 mb-5 text-[2.75rem] font-[570] leading-[0.9] tracking-[-0.07em] text-[var(--color-ink)]",
        "[&_span]:text-[0.82rem] [&_span]:font-medium [&_span]:tracking-normal [&_span]:text-[var(--color-fg-muted)]",
        className,
      )}
      {...props}
    />
  );
}

/** Unavailable Fix Pack status — replaces `.saas-price-status`. */
export function SaasPriceStatus({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "saas-price-status mt-auto grid w-full gap-2.5 self-start border-t border-[var(--color-border)] pt-4",
        className,
      )}
      {...props}
    />
  );
}

/** Dashed unavailable pill — replaces `.saas-price-status__pill`. */
export function SaasPriceStatusPill({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "saas-price-status__pill inline-flex min-h-12 items-center justify-center rounded-[10px] border border-dashed border-[var(--color-border)] bg-[color-mix(in_srgb,var(--color-paper)_82%,var(--color-border))] px-3.5 text-sm font-[650] text-[var(--color-ink)]",
        className,
      )}
      {...props}
    />
  );
}

/** Public 48px section title — replaces `.section-title`. */
export function MarketingSectionTitle({
  className,
  as: Tag = "h2",
  ...props
}: React.ComponentProps<"h2"> & { as?: "h1" | "h2" | "h3" }) {
  return (
    <Tag
      className={cn(
        "section-title mb-4 font-sans text-[48px] font-semibold leading-[1.04] tracking-[-0.05em] text-[var(--color-ink)] max-[834px]:text-[36px] max-[480px]:text-[30px]",
        "[&_em]:font-normal [&_em]:italic",
        className,
      )}
      {...props}
    />
  );
}

/** Secondary home action classes — compose onto `Link` or `<a>`. */
export const saasSecondaryClassName = cn(
  "saas-secondary mt-auto inline-flex min-h-12 items-center justify-center gap-2 self-start rounded-[10px] border border-[var(--color-border)] bg-[var(--color-paper)] px-3.5 text-sm font-[650] text-[var(--color-ink)] no-underline",
  "transition-[transform,border-color] duration-[var(--duration-base)] ease-[var(--ease-premium)] hover:-translate-y-px hover:border-[color-mix(in_srgb,var(--color-focus)_36%,var(--color-border))]",
  "active:translate-y-px active:scale-[0.98]",
  "focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-[color-mix(in_srgb,var(--color-focus)_28%,transparent)]",
  "motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:active:translate-y-0 motion-reduce:active:scale-100",
  "aria-disabled:cursor-not-allowed aria-disabled:text-[var(--color-fg-muted)]",
);

/** Ink pill primary — replaces leftover `.saas-primary` / audit URL CTAs. */
export const saasPrimaryClassName = cn(
  "saas-primary inline-flex min-h-12 min-w-max items-center justify-center gap-2 rounded-full border border-[var(--color-ink)] bg-[var(--color-ink)] px-[18px] text-sm font-[650] text-[var(--color-paper)] no-underline shadow-none",
  "transition-[transform,background,border-color,box-shadow] duration-[var(--duration-base)] ease-[var(--ease-premium)]",
  "hover:bg-[color-mix(in_srgb,var(--color-ink)_88%,var(--color-paper))] hover:shadow-none hover:-translate-y-px",
  "active:translate-y-px active:scale-[0.98]",
  "disabled:cursor-not-allowed disabled:border-[var(--color-border)] disabled:bg-[color-mix(in_srgb,var(--color-paper)_88%,var(--color-border))] disabled:text-[var(--color-fg-muted)] disabled:opacity-100",
  "focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-[color-mix(in_srgb,var(--color-focus)_28%,transparent)]",
  "motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:active:translate-y-0 motion-reduce:active:scale-100",
);

/** Tight field stack class — compose onto `FieldGroup`. */
export const saasHeroFieldGroupClassName = "saas-hero-field-group gap-0";

/** URL field label class — compose onto `FieldLabel`. */
export const saasAuditLabelClassName =
  "saas-audit-label mb-2.5 text-[0.8125rem] font-[650] text-[var(--color-ink)]";

/** URL field label — replaces leftover `.saas-audit-label`. */
export function SaasAuditLabel({ className, ...props }: React.ComponentProps<"label">) {
  return <label className={cn(saasAuditLabelClassName, className)} {...props} />;
}

/** Raised URL field class — compose onto `InputGroup`. */
export const saasInputWrapClassName = cn(
  "saas-input-wrap min-h-[68px] rounded-[14px] border border-[color-mix(in_srgb,var(--color-border)_88%,var(--color-ink))] bg-[var(--color-paper)] shadow-[var(--elevation-3)]",
  "focus-within:border-[color-mix(in_srgb,var(--color-focus)_65%,var(--color-paper))]",
  "focus-within:shadow-[0_0_0_4px_color-mix(in_srgb,var(--color-focus)_11%,transparent),var(--elevation-3)]",
  "[&_[data-slot=input-group-control]]:min-h-[66px] [&_[data-slot=input-group-control]]:pl-[50px] [&_[data-slot=input-group-control]]:text-base [&_[data-slot=input-group-control]]:text-[var(--color-ink)]",
  "[&_[data-slot=input-group-addon]]:text-[var(--color-fg-muted)]",
  "[&_[data-slot=input-group-addon]_svg]:size-[18px] [&_[data-slot=input-group-addon]_svg]:stroke-[1.7]",
  "max-[680px]:flex max-[680px]:h-auto max-[680px]:min-h-0 max-[680px]:flex-wrap",
  "max-[680px]:[&_[data-slot=input-group-control]]:min-h-14 max-[680px]:[&_[data-slot=input-group-control]]:flex-[1_1_190px] max-[680px]:[&_[data-slot=input-group-control]]:pl-[46px]",
);

/** Raised URL field wrap — replaces leftover `.saas-input-wrap`. */
export function SaasInputWrap({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn(saasInputWrapClassName, className)} {...props} />;
}

/** Trailing URL action class — compose onto `InputGroupAddon`. */
export const saasInputActionClassName = cn(
  "saas-input-action p-[7px]",
  "[&_[data-slot=button]]:rounded-full [&_[data-slot=button]]:border [&_[data-slot=button]]:border-[var(--color-ink)] [&_[data-slot=button]]:bg-[var(--color-ink)] [&_[data-slot=button]]:text-[var(--color-paper)] [&_[data-slot=button]]:shadow-none",
  "[&_[data-slot=button]:hover:not(:disabled)]:bg-[color-mix(in_srgb,var(--color-ink)_88%,var(--color-paper))]",
  "[&_[data-slot=button]:disabled]:cursor-not-allowed [&_[data-slot=button]:disabled]:border-[color-mix(in_srgb,var(--color-ink)_22%,var(--color-border))] [&_[data-slot=button]:disabled]:bg-[var(--color-paper)] [&_[data-slot=button]:disabled]:text-[var(--color-fg-muted)] [&_[data-slot=button]:disabled]:opacity-100",
  "[&_[data-slot=button]:active:not(:disabled)]:translate-y-px [&_[data-slot=button]:active:not(:disabled)]:scale-[0.98]",
  "motion-reduce:[&_[data-slot=button]]:transition-none",
  "max-[680px]:w-full max-[680px]:flex-[0_0_100%] max-[680px]:px-[7px] max-[680px]:pt-0 max-[680px]:pb-[7px]",
  "max-[680px]:[&_.saas-primary]:min-h-12 max-[680px]:[&_.saas-primary]:w-full",
);

/** Trailing URL action — replaces leftover `.saas-input-action`. */
export function SaasInputAction({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn(saasInputActionClassName, className)} {...props} />;
}

/* ── Product surfaces (Step 4) — /seo-audit hero ─────────────────────────── */

/** Soft Spatial audit hero — lime mist + quiet desk floor. */
export function AuditHero({ className, ...props }: React.ComponentProps<"section">) {
  return (
    <section
      className={cn(
        "audit-hero-v2 relative isolate overflow-x-clip border-b border-[var(--color-border)] bg-transparent py-[clamp(28px,4.5vw,64px)] max-[700px]:pt-10",
        "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-[radial-gradient(circle_at_18%_8%,color-mix(in_srgb,var(--color-accent)_14%,transparent),transparent_29rem),radial-gradient(circle_at_82%_12%,color-mix(in_srgb,var(--color-focus)_5%,transparent),transparent_26rem)] before:content-['']",
        className,
      )}
      {...props}
    />
  );
}

/** Audit hero column — replaces leftover `.audit-hero-v2__inner`. */
export function AuditHeroInner({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "audit-hero-v2__inner relative mx-auto w-[min(1180px,calc(100%-40px))]",
        className,
      )}
      {...props}
    />
  );
}

/** Audit hero grid — replaces leftover `.audit-hero-v2__grid`. */
export function AuditHeroGrid({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "audit-hero-v2__grid grid grid-cols-1 items-start gap-x-[clamp(34px,7vw,104px)] gap-y-[clamp(20px,3vw,36px)]",
        className,
      )}
      {...props}
    />
  );
}

/** Audit hero copy + inherit headline — replaces leftover `.audit-hero-v2__copy`. */
export function AuditHeroCopy({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "audit-hero-v2__copy max-w-[670px] text-left",
        "[&_h1]:mt-[21px] [&_h1]:mb-0 [&_h1]:max-w-[12.8ch] [&_h1]:font-[inherit] [&_h1]:text-[clamp(3.1rem,5.3vw,4.72rem)] [&_h1]:font-[590] [&_h1]:leading-[0.98] [&_h1]:tracking-[-0.072em] [&_h1]:text-[var(--color-ink)]",
        "[&_h1_em]:inline [&_h1_em]:font-[inherit] [&_h1_em]:font-normal [&_h1_em]:italic [&_h1_em]:text-[var(--color-ink)]",
        "[&>p]:mt-[23px] [&>p]:mb-0 [&>p]:max-w-[610px] [&>p]:text-[clamp(1rem,1.35vw,1.15rem)] [&>p]:leading-[1.68] [&>p]:tracking-[-0.016em] [&>p]:text-[var(--color-fg-muted)]",
        "max-[700px]:[&_h1]:max-w-none max-[700px]:[&_h1]:text-[clamp(2.5rem,10.7vw,2.85rem)] max-[700px]:[&_h1]:tracking-[-0.068em]",
        "max-[700px]:[&>p]:mt-[19px] max-[700px]:[&>p]:text-[0.98rem] max-[700px]:[&>p]:leading-[1.58]",
        className,
      )}
      {...props}
    />
  );
}

/** Lime-dot mono eyebrow — replaces leftover `.audit-hero-v2__eyebrow`. */
export function AuditHeroEyebrow({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "audit-hero-v2__eyebrow inline-flex items-center gap-[9px] font-mono text-[0.6875rem] font-[600] uppercase leading-none tracking-[0.12em] text-[var(--color-fg-muted)]",
        "[&>span]:size-[6px] [&>span]:rounded-full [&>span]:bg-neutral-400 dark:[&>span]:bg-neutral-500 [&>span]:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-ink)_6%,transparent)]",
        className,
      )}
      {...props}
    />
  );
}

/** Audit URL form slot — hook class kept for leftover focused-form CSS. */
export function AuditHeroForm({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "audit-hero-v2__form mt-[26px] block w-[min(760px,100%)]",
        className,
      )}
      {...props}
    />
  );
}

/** Lime-dot proof row — replaces leftover `.audit-hero-v2__proof`. */
export function AuditHeroProof({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "audit-hero-v2__proof mt-[26px] flex max-w-[610px] flex-wrap gap-x-[18px] gap-y-2 border-0 bg-transparent shadow-none",
        "[&>span]:inline-flex [&>span]:min-h-0 [&>span]:items-center [&>span]:bg-transparent [&>span]:p-0 [&>span]:text-xs [&>span]:leading-[1.45] [&>span]:text-[var(--color-fg-muted)]",
        "[&>span]:before:mr-[7px] [&>span]:before:size-1 [&>span]:before:rounded-full [&>span]:before:bg-[var(--color-accent)] [&>span]:before:content-['']",
        "max-[700px]:mt-[21px] max-[700px]:gap-x-[13px] max-[700px]:gap-y-1.5",
        className,
      )}
      {...props}
    />
  );
}

/** Hash CTA under the audit form — replaces leftover `.audit-hero-v2__text-link`. */
export function AuditHeroTextLink({ className, ...props }: React.ComponentProps<"a">) {
  return (
    <a
      className={cn(
        "audit-hero-v2__text-link mt-4 inline-flex min-h-12 items-center gap-2 text-sm font-[650] text-[var(--color-ink)] underline underline-offset-4",
        "[&_svg]:size-4 [&_svg]:transition-transform [&_svg]:duration-[180ms] [&_svg]:ease-[var(--ease-premium)]",
        "hover:text-[var(--color-ink)] hover:[&_svg]:translate-x-0.5 hover:[&_svg]:-translate-y-0.5",
        "motion-reduce:[&_svg]:transition-none",
        className,
      )}
      {...props}
    />
  );
}

/** Account remaining-copy — replaces leftover `.audit-hero-v2__account-note`. */
export function AuditHeroAccountNote({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      className={cn(
        "audit-hero-v2__account-note mt-[15px] min-h-5 text-center text-xs text-[var(--color-fg-muted)]",
        "[&_a]:text-[var(--color-badge-blue-fg)] [&_a]:underline-offset-[3px]",
        className,
      )}
      {...props}
    />
  );
}
